import * as THREE from 'three';
import { ASSETS, WORLD_RADIUS } from '../config';
import { AssetLoader } from '../io/AssetLoader';
import { loadTexture } from '../io/textures';
import { createOceanMaterial, createTerrainMaterial } from './materials';
import { CityLights } from './CityLights';

/**
 * The globe: real baked terrain meshes + ocean mesh rendered with ports of the upstream
 * `Custom/Terrain` and `Custom/Ocean` shaders.
 *
 * Meshes are pre-generated by the Unity project and shipped in the `MeshSerializer`
 * container (see src/io/AssetLoader.ts). Each terrain mesh is named after a country, which
 * later phases use for highlighting and country lookup.
 */
export class Globe {
  readonly radius = WORLD_RADIUS;
  readonly group = new THREE.Group();
  readonly terrainGroup = new THREE.Group();
  readonly oceanGroup = new THREE.Group();

  readonly terrainMaterial: THREE.ShaderMaterial;
  readonly oceanMaterial: THREE.ShaderMaterial;

  cityLights?: CityLights;
  private readonly textures: THREE.Texture[];
  private readonly meshes: THREE.Mesh[] = [];
  private readonly raycaster = new THREE.Raycaster();

  private constructor(
    terrainMaterial: THREE.ShaderMaterial,
    oceanMaterial: THREE.ShaderMaterial,
    textures: THREE.Texture[],
  ) {
    this.terrainMaterial = terrainMaterial;
    this.oceanMaterial = oceanMaterial;
    this.textures = textures;
    // Unity is left-handed, three.js is right-handed. Mirror the globe (and its shaders,
    // which use modelMatrix) so the world is not reversed. three flips face winding for
    // negative-determinant objects automatically.
    this.group.scale.set(1, 1, -1);
    this.group.add(this.terrainGroup, this.oceanGroup);
  }

  static async create(): Promise<Globe> {
    const [
      terrainMeshes,
      oceanMeshes,
      colourWest,
      colourEast,
      normalWest,
      normalEast,
      lightMap,
      lakes,
      waveNormalA,
      waveNormalB,
      oceanCol,
      shoreDst,
      noise,
      cityLightData,
    ] = await Promise.all([
      AssetLoader.loadMeshFile(ASSETS.meshes.terrainLow),
      AssetLoader.loadMeshFile(ASSETS.meshes.ocean),
      loadTexture(ASSETS.tex.colourWest),
      loadTexture(ASSETS.tex.colourEast),
      loadTexture(ASSETS.tex.normalWest),
      loadTexture(ASSETS.tex.normalEast),
      loadTexture(ASSETS.tex.lightMap),
      loadTexture(ASSETS.tex.lakes),
      loadTexture(ASSETS.tex.waveNormalA, { repeat: true }),
      loadTexture(ASSETS.tex.waveNormalB, { repeat: true }),
      loadTexture(ASSETS.tex.oceanCol),
      loadTexture(ASSETS.tex.shoreDst),
      loadTexture(ASSETS.tex.noise, { repeat: true }),
      fetch('assets/data/city_lights.bin').then((r) => r.arrayBuffer()).then((b) => new Float32Array(b)),
    ]);

    const textures = [
      colourWest, colourEast, normalWest, normalEast, lightMap, lakes,
      waveNormalA, waveNormalB, oceanCol, shoreDst, noise,
    ];

    const terrainMaterial = createTerrainMaterial({
      colourWest, colourEast, normalWest, normalEast, lightMap, lakes, waveNormalA,
    });
    const oceanMaterial = createOceanMaterial({
      oceanCol, noise, waveNormalA, waveNormalB, shoreDst,
    });

    const globe = new Globe(terrainMaterial, oceanMaterial, textures);

    for (const data of terrainMeshes) {
      const mesh = new THREE.Mesh(AssetLoader.toGeometry(data), terrainMaterial);
      mesh.name = data.name;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      globe.meshes.push(mesh);
      globe.terrainGroup.add(mesh);
    }

    for (const data of oceanMeshes) {
      const mesh = new THREE.Mesh(AssetLoader.toGeometry(data), oceanMaterial);
      mesh.name = data.name;
      mesh.receiveShadow = true;
      globe.meshes.push(mesh);
      globe.oceanGroup.add(mesh);
    }

    console.info(
      `Globe loaded: ${terrainMeshes.length} terrain meshes, ${oceanMeshes.length} ocean meshes`,
    );

    globe.cityLights = new CityLights(cityLightData);
    globe.group.add(globe.cityLights.object);

    return globe;
  }

  /** Advance animated shader state (ocean waves, foam). */
  update(elapsedSeconds: number): void {
    this.oceanMaterial.uniforms.uTime.value = elapsedSeconds;
  }

  /** Set the direction from the surface toward the sun (drives day/night shading). */
  setSunDirection(direction: THREE.Vector3): void {
    this.terrainMaterial.uniforms.uSunDir.value.copy(direction).normalize();
    this.oceanMaterial.uniforms.uSunDir.value.copy(direction).normalize();
    this.cityLights?.setSunDirection(direction);
  }

  /** Feed the directional light's shadow map into the terrain and ocean shaders. */
  updateShadow(light: THREE.DirectionalLight): void {
    // three renders shadow depth into `shadow.map.depthTexture` (a DepthTexture); the
    // render target's colour texture is unused for BasicShadowMap/PCFShadowMap.
    const map = light.shadow.map?.depthTexture ?? null;
    for (const material of [this.terrainMaterial, this.oceanMaterial]) {
      material.uniforms.uShadowMap.value = map;
      material.uniforms.uShadowMatrix.value.copy(light.shadow.matrix);
      material.uniforms.uShadowEnabled.value = map ? 1 : 0;
    }
  }

  /**
   * Raycast the terrain straight down at a unit-sphere direction.
   * Returns the surface point and the name of the country mesh it hit
   * (used for package landing and terrain height lookups).
   */
  sampleSurface(unitDir: THREE.Vector3): { point: THREE.Vector3; countryName: string } | null {
    const origin = this.raycasterOrigin.copy(unitDir).multiplyScalar(this.radius + 80);
    this.raycaster.set(origin, this.raycasterDir.copy(unitDir).negate());
    this.raycaster.far = 200;
    const hits = this.raycaster.intersectObjects(this.terrainGroup.children, false);
    if (hits.length === 0) return null;
    return { point: hits[0].point.clone(), countryName: hits[0].object.name };
  }

  private readonly raycasterOrigin = new THREE.Vector3();
  private readonly raycasterDir = new THREE.Vector3();

  dispose(): void {
    for (const mesh of this.meshes) mesh.geometry.dispose();
    for (const texture of this.textures) texture.dispose();
    this.terrainMaterial.dispose();
    this.oceanMaterial.dispose();
  }
}
